import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, CircularProgress, Alert,
  List, ListItem, ListItemText, Divider, Card, CardContent, Grid, Chip
} from '@mui/material';
import AdminLayout from '../../components/admin/AdminLayout';
import { useAuth } from '@vortx/shared-ui';
import { API_BASE_URL } from '../../services/api';

const SettingsPage = () => {
  const { currentUser, userProfile } = useAuth();
  const [companyInfo, setCompanyInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchCompanyInfo = useCallback(async () => {
    // Wait for userProfile to be available
    if (!userProfile) {
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // Try to fetch company details from API
      let apiCompanyData = null;
      if (currentUser) {
        try {
          const token = await currentUser.getIdToken();
          const response = await fetch(`${API_BASE_URL}/api/v1/admin/my-company`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });

          if (response.ok) {
            apiCompanyData = await response.json();
          } else if (response.status === 401 || response.status === 403) {
            // Authentication/authorization errors should be surfaced
            throw new Error(`Access denied: ${response.status === 401 ? 'Not authenticated' : 'Not authorized'} to view company settings`);
          } else if (response.status >= 500) {
            // Server errors should be surfaced
            throw new Error(`Server error loading company data (${response.status}). Please try again later.`);
          }
          // 404 is acceptable - API endpoint may not exist, use userProfile
        } catch (apiErr) {
          // Re-throw if it's our own error (auth/server errors)
          if (apiErr.message.includes('Access denied') || apiErr.message.includes('Server error')) {
            throw apiErr;
          }
          // Network errors are acceptable - log and continue with userProfile
          console.log('Company API not available due to network error, using profile data');
        }
      }

      // Validate that we have the required data from either source
      const name = apiCompanyData?.name || userProfile.company_name;
      const id = apiCompanyData?.id || userProfile.company_id;

      if (!name) {
        throw new Error('Company name is missing. Please contact support to fix your account configuration.');
      }
      if (!id) {
        throw new Error('Company ID is missing. Please contact support to fix your account configuration.');
      }

      // Use API data if available, otherwise use userProfile
      // Boolean fields default to false, arrays default to empty - these are valid defaults
      setCompanyInfo({
        name: name,
        id: id,
        plan: apiCompanyData?.site_dna_plan || userProfile.site_dna_plan || null,
        add_ons: apiCompanyData?.site_dna_add_ons || userProfile.site_dna_add_ons || [],
        is_platform_owner: apiCompanyData?.is_platform_owner || userProfile.is_platform_owner || false,
        is_super_admin: userProfile.is_super_admin || false,
        is_company_admin: userProfile.is_company_admin || false
      });
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [currentUser, userProfile]);

  useEffect(() => {
    fetchCompanyInfo();
  }, [fetchCompanyInfo]);

  // Determine user role display
  const getUserRole = () => {
    if (companyInfo?.is_super_admin) {
      return 'Super Admin';
    }
    if (companyInfo?.is_company_admin) {
      return 'Company Admin';
    }
    return 'User';
  };

  if (loading) {
    return (
      <AdminLayout title="Company Settings">
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout title="Company Settings">
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {/* Platform Owner Badge */}
      {companyInfo?.is_platform_owner && (
        <Alert
          severity="info"
          sx={{
            mb: 3,
            backgroundColor: 'rgba(255, 193, 7, 0.1)',
            border: '1px solid #ffc107',
            '& .MuiAlert-icon': { color: '#f57c00' }
          }}
          icon={
            <Chip
              label="Platform Access"
              sx={{
                backgroundColor: '#ffc107',
                color: '#000',
                fontWeight: 'bold',
                fontSize: '0.85rem'
              }}
            />
          }
        >
          <Typography variant="body2" sx={{ mt: 0.5 }}>
            Your company has <strong>Platform Owner</strong> status, granting full access to all products and features across the platform.
          </Typography>
        </Alert>
      )}

      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>Company Information</Typography>
              <List>
                <ListItem>
                  <ListItemText
                    primary={
                      <Typography variant="h5" component="div" sx={{ fontWeight: 500 }}>
                        {companyInfo?.name}
                      </Typography>
                    }
                    secondary="Company Name"
                  />
                </ListItem>
                <Divider />
                <ListItem>
                  <ListItemText primary="Company ID" secondary={companyInfo?.id} />
                </ListItem>
                <Divider />
                <ListItem>
                  <ListItemText
                    primary="Your Role"
                    secondary={
                      <Chip
                        label={getUserRole()}
                        size="small"
                        color={companyInfo?.is_super_admin ? 'error' : companyInfo?.is_company_admin ? 'primary' : 'default'}
                        sx={{ mt: 0.5 }}
                      />
                    }
                  />
                </ListItem>
              </List>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                {companyInfo?.is_platform_owner ? 'Access Level' : 'Subscription'}
              </Typography>

              {companyInfo?.is_platform_owner ? (
                <Box sx={{ py: 2 }}>
                  <Chip
                    label="Full Platform Access"
                    sx={{
                      backgroundColor: '#ffc107',
                      color: '#000',
                      fontWeight: 'bold',
                      fontSize: '1rem',
                      py: 2,
                      px: 1
                    }}
                  />
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                    As a Platform Owner, you have unrestricted access to all products, features, and add-ons without subscription limitations.
                  </Typography>
                </Box>
              ) : (
                <List>
                  <ListItem>
                    <ListItemText primary="Plan" secondary={companyInfo?.plan || 'No plan configured'} />
                  </ListItem>
                  <Divider />
                  <ListItem>
                    <ListItemText
                      primary="Add-ons"
                      secondary={companyInfo?.add_ons?.length > 0 ? companyInfo.add_ons.join(', ') : 'None'}
                    />
                  </ListItem>
                </List>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Box sx={{ mt: 3 }}>
        <Typography variant="body2" color="text.secondary">
          {companyInfo?.is_platform_owner
            ? 'Platform owner settings are managed at the platform level.'
            : 'Contact your administrator to update company settings or subscription details.'}
        </Typography>
      </Box>
    </AdminLayout>
  );
};

export default SettingsPage;
